import {
  PaymentElement,
  useElements,
  useStripe
} from "@stripe/react-stripe-js";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams, useRouteLoaderData } from "react-router";

import {
  Alert,
  Button,
  Card,
  Heading,
  Hr,
  Span,
  TelephoneMask,
  TextInput
} from "@icg360/design-system";

import { AppLoaderData } from "../../../types";
import styles from "../LiftLock.module.css";
import { OrderSummary } from "../order-summary/OrderSummary";
import { usePaymentIntentStatus } from "../usePaymentIntentStatus";

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

interface ContactFormData {
  contactName: string;
  contactEmailAddress: string;
  contactPhone: string;
}

export const LiftLockForm = () => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { quote, sectionFieldsMap } =
    useRouteLoaderData<AppLoaderData>("root")!;
  const { proposal } = useParams();
  const formRef = useRef<HTMLFormElement>(null);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const status = usePaymentIntentStatus(
    quote.roofResealingStripeIntentClientSecret
  );

  useEffect(() => {
    if (status && ["succeeded", "processing"].includes(status)) {
      void navigate(`/${proposal}/liftlock/return`);
    } else if (status && status !== "requires_payment_method") {
      void navigate(`/${proposal}`);
    }
  }, [navigate, proposal, status]);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid: isContactFormValid }
  } = useForm<ContactFormData>({
    defaultValues: { contactName: sectionFieldsMap.customerName },
    mode: "onBlur"
  });

  const submitPayment = async (data: ContactFormData) => {
    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    // Update the metadata on the payment intent with the contact information
    const metadataEndpoint = `${API_BASE_URL}/api/rest/v1/stripe/paymentintent/update/${proposal}`;
    const res = await fetch(metadataEndpoint, {
      method: "PATCH",
      body: JSON.stringify(data)
    });

    if (res.status !== 200) {
      setErrorMessage(
        "Try reloading the page. If the problem continues, reach out to your insurance representative."
      );
      setIsLoading(false);
      return;
    }

    // Submit the payment details
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/${proposal}/liftlock/return`
      }
    });

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (error.type === "card_error" || error.type === "validation_error") {
      setErrorMessage(error?.message ?? null);
    } else if (error.payment_intent?.status === "canceled") {
      setErrorMessage(
        "Please reload the page and check your card information. If the problem continues, reach out to your insurance representative."
      );
      console.error(error);
    } else {
      setErrorMessage(
        "Please check your card information and your internet connection before trying again."
      );
      console.error(error);
    }

    setIsLoading(false);
  };

  const submitContactInfoForm = () => {
    // turn a button click in a child component outside the form into a form submit
    formRef.current?.dispatchEvent(
      new Event("submit", { cancelable: true, bubbles: true })
    );
  };

  const isPaymentButtonDisabled =
    isLoading || !stripe || !elements || !isContactFormValid;

  return (
    <>
      <div className={styles.container}>
        <Button
          appearance="tertiary"
          leadingIcon="Back"
          className={styles.backButtonDS}
          size="sm"
          onClick={() => void navigate(`/${proposal}`)}
        >
          Back to SageSure quote
        </Button>
        {errorMessage ? (
          <Alert
            appearance="danger"
            description={errorMessage}
            title="Payment failed."
            className={styles.errorAlert}
            onDismiss={() => setErrorMessage(null)}
            dismissable
          />
        ) : null}
        <div className={styles.formContainer}>
          <Card className={styles.formCard}>
            <Heading size="md" className={styles.formHeading}>
              Payment method
            </Heading>
            <PaymentElement />
            <Hr spacing="lg" />
            <div className={styles.formHeading}>
              <Heading size="md">Contact information</Heading>
              <Span>
                LiftLock LLC will use the following information to contact you
                at time of service.
              </Span>
            </div>
            <form
              onSubmit={e => void handleSubmit(submitPayment)(e)}
              className={styles.inputFieldWrapper}
              ref={formRef}
            >
              <TextInput
                data-testid="nameInput"
                label="Your name"
                isError={!!errors.contactName}
                errorMessage={errors.contactName?.message}
                {...register("contactName", {
                  required: "Please enter the name of the person to contact."
                })}
                isRequired
              />
              <TextInput
                data-testid="emailInput"
                type="email"
                label="Email"
                isError={!!errors.contactEmailAddress}
                errorMessage={errors.contactEmailAddress?.message}
                {...register("contactEmailAddress", {
                  required: "Please enter an email.",
                  pattern: {
                    value: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                    message: "Please enter a valid email address."
                  }
                })}
                isRequired
              />
              <TelephoneMask
                data-testid="telephoneInput"
                label="Phone"
                {...register("contactPhone", {
                  required: "Please enter a phone number.",
                  pattern: {
                    value: /^(\+0?1\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
                    message: "Please enter a valid 10 digit phone number."
                  }
                })}
                errorMessage={errors.contactPhone?.message}
                isError={!!errors.contactPhone}
                isRequired
              />
            </form>
            <Hr spacing="lg" />
            <Span color="quiet" size="sm">
              If you change your mind or don&apos;t end up having the work done
              you can request a full refund.
            </Span>
          </Card>
          <div className={styles.orderCardWrapper}>
            {quote.roofResealingPaymentAmount ? (
              <OrderSummary
                isLoading={isLoading}
                isDisabled={isPaymentButtonDisabled}
                price={quote.roofResealingPaymentAmount}
                onSubmitPayment={submitContactInfoForm}
              />
            ) : null}
          </div>
        </div>
      </div>
      <div className={styles.mobileOrderSummary}>
        {quote.roofResealingPaymentAmount ? (
          <OrderSummary
            isLoading={isLoading}
            isDisabled={isPaymentButtonDisabled}
            price={quote.roofResealingPaymentAmount}
            onSubmitPayment={submitContactInfoForm}
            collapsible
          />
        ) : null}
      </div>
    </>
  );
};
